// ============================================================
// The body plate's figure art (plan body-map, Track A) - what one traced figure
// is as data, and the reader that checks it at the boundary.
//
// Each figure is an asset in src/assets/plates/<id>.json, written by
// tools/plate-art/split.py from the user's text-prompted drawings: the line art
// and its solid silhouette as plain path lists (rendered as <path d>, never as
// injected markup), the six hit regions fitted to the drawn seams (a region may
// be several polygons), anchors for the callouts, the eye points, and the
// figure's own DISPLAY names (Right Fore, Upper Tail). Those names are for the
// plate only: conditions (Cr:/De:) and the critical tables key on the engine's
// location names in bodyConfig, which never change.
//
// Coordinates are the figure's frame (2123 x 3000 units, y down).
//
// Pure. Zero React dependency.
// ============================================================

export const PLATE_FIGURE_IDS = [
  'human',
  'astartes',
  'beast',
  'xenos',
  'xenobeast',
  'machine',
  'xenos4a',
  'machine4a',
  'xenostail',
  'xenostail4a',
  'robot',
  'machinetrack',
  'machinetrack4a',
  'daemon',
] as const;
export type PlateFigureId = (typeof PLATE_FIGURE_IDS)[number];

type PlateLocationId = 'head' | 'body' | 'rightArm' | 'leftArm' | 'rightLeg' | 'leftLeg';
/** The locations drawn as polygons. The Body is never drawn: it is whatever the other five leave (even-odd). */
const PLATE_DRAWN_REGIONS = ['head', 'rightArm', 'leftArm', 'rightLeg', 'leftLeg'] as const;
type PlateDrawnRegion = (typeof PLATE_DRAWN_REGIONS)[number];

/** How the figure stands at rest (ruling 2026-10-09): sway, a planted prowl, a servo, a float. */
export type IdleKind = 'stand' | 'prowl' | 'twitch' | 'float';
const IDLE_KINDS: readonly IdleKind[] = ['stand', 'prowl', 'twitch', 'float'];

type Point = [number, number];

/** Line art as data: one transform over a list of path `d` strings. */
export interface PathGroup {
  transform: string;
  paths: string[];
}

export interface PlateArt {
  id: PlateFigureId;
  name: string;
  art: PathGroup;
  silhouette: PathGroup;
  /** Each drawn location's polygon pieces ("x,y x,y ..."); the Body is the rest. */
  regions: Record<PlateDrawnRegion, string[]>;
  anchors: Record<PlateLocationId, Point>;
  /**
   * The Body's box [x0, y0, x1, y1] - its hit area (plateParts `regionsOf`), the torso the drawn regions leave. Not the
   * whole figure: that is `figureBounds`.
   */
  box: [number, number, number, number];
  /** Two eye points, or null for a figure without a readable face. */
  eyes: [Point, Point] | null;
  /** Where Blinded / Deafened stop on a helmeted head (view y), or null for the whole head. */
  eyeLine: number | null;
  skullLine: number | null;
  /** Line weight and glow multipliers (dense machine art draws thinner). */
  lineWeight: number;
  glow: number;
  /** Display-only overrides per location: name, callout side, callout row. */
  names: Partial<Record<PlateLocationId, string>>;
  sides: Partial<Record<PlateLocationId, 'L' | 'R'>>;
  rows: Partial<Record<PlateLocationId, number>>;
  idle: IdleKind;
  /** The daemon figure carries the Daemonic look by nature. */
  daemon: boolean;
  /** The outer contour, fine and coarse (GeoJSON-like rings), for outline effects. */
  outline: { fine: unknown; coarse: unknown };
}

const fail = (what: string): never => {
  throw new Error(`not plate art: ${what}`);
};
export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** A path group read back, or a sentence naming what is wrong (shared with the vehicle reader). */
export function pathGroup(v: unknown, what: string): PathGroup {
  const ok = isRecord(v) && typeof v.transform === 'string' && Array.isArray(v.paths) && v.paths.length > 0;
  return ok ? (v as unknown as PathGroup) : fail(`${what} needs a transform and paths`);
}

function regions(v: unknown): Record<PlateDrawnRegion, string[]> {
  if (!isRecord(v)) return fail('regions');
  for (const id of PLATE_DRAWN_REGIONS) {
    const pieces = v[id];
    if (!Array.isArray(pieces) || pieces.length === 0) fail(`region ${id}`);
  }
  return v as Record<PlateDrawnRegion, string[]>;
}

/**
 * A figure asset read back, checked once at the boundary so the plate can trust it.
 * Throws a plain sentence naming what is wrong; never returns a partial figure.
 */
export function readPlateArt(raw: unknown): PlateArt {
  if (!isRecord(raw)) return fail('not an object');
  if (!PLATE_FIGURE_IDS.includes(raw.id as PlateFigureId)) fail(`unknown figure id ${String(raw.id)}`);
  if (!IDLE_KINDS.includes(raw.idle as IdleKind)) fail(`unknown idle kind ${String(raw.idle)}`);
  return {
    ...(raw as unknown as PlateArt),
    art: pathGroup(raw.art, 'art'),
    silhouette: pathGroup(raw.silhouette, 'silhouette'),
    regions: regions(raw.regions),
  };
}

/** A polygon piece's points, from its "x,y x,y ..." string. */
export const piecePoints = (piece: string): [number, number][] =>
  piece
    .trim()
    .split(/\s+/)
    .map((q) => q.split(',').map(Number) as [number, number]);

/** A region's box [x0, y0, x1, y1] in the frame, from its polygon pieces ("x,y x,y ..."). */
export function regionBox(pieces: readonly string[]): [number, number, number, number] {
  const points = pieces.flatMap(piecePoints);
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

type Box = [number, number, number, number];
type ReadonlyBox = readonly [number, number, number, number];

/**
 * A struck part's box: its drawn region's, or - for what the drawn regions leave (a person's Body, a vehicle's Hull) -
 * the art's own box (a person's Body box; a vehicle's whole drawing).
 */
export function partBox(
  regions: Partial<Record<string, readonly string[]>>,
  box: ReadonlyBox,
  part: string,
): ReadonlyBox {
  const pieces = regions[part];
  return pieces?.length ? regionBox(pieces) : box;
}

/**
 * The whole figure's bounds: every drawn region and the Body together (where glances pass and the ground lies). A
 * vehicle's art has the same two fields, so it takes them too.
 */
export function figureBounds(art: { regions: Readonly<Record<string, readonly string[]>>; box: ReadonlyBox }): Box {
  const boxes = [
    art.box,
    ...Object.values(art.regions)
      .filter((p) => p.length)
      .map(regionBox),
  ];
  return [
    Math.min(...boxes.map((b) => b[0])),
    Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])),
    Math.max(...boxes.map((b) => b[3])),
  ];
}
